import { createContext, useState, ReactNode, useContext } from 'react'
import { Instrument, InstrumentType, Song, SONG_TYPES, TouchMode } from '../../utils/types'
import { useChangeArrangement, useChangeInstrumentMode, usePostMqttChangeSong, usePostSetInstrumentMode } from '../../mqtt/mqttHandlerContext'
import { useCurrentUser } from './currentUserContext'
import { useConnectedInstruments, useUpdateConnectedInstrument } from './connectedInstrumentsContext'
import { buttonSounds, hybridSounds, joystickSounds, touchSounds } from '../../utils/api'

export type ChangeCurrentSong = ({ songId, songType, instrumentSound, deviceId }: { songId?: string; songType?: SONG_TYPES; instrumentSound?: string; deviceId?: string }) => void

interface CurrentSongContextProps {
    currentSong: Song | undefined
    changeCurrentSong: ChangeCurrentSong
}

const CurrentSongContext = createContext<CurrentSongContextProps>({
    currentSong: undefined,
    changeCurrentSong: () => {}
})

export const CurrentSongProvider = ({ children }: { children: ReactNode }) => {
    const [currentSong, setCurrentSong] = useState<Song | undefined>(undefined)
    // const postMqttChangeSong = usePostMqttChangeSong()
    const changeArrangement = useChangeArrangement()
    const currentUser = useCurrentUser()
    const connectedInstruments = useConnectedInstruments()
    const updateConnectedInstruments = useUpdateConnectedInstrument()
    // const postSetInstrumentMode = usePostSetInstrumentMode()
    const changeInstrumentMode = useChangeInstrumentMode()

    function getDefaultSound(type: InstrumentType) {
        if (type === 'joysticks') {
            return joystickSounds[0].id
        }
        if (type === 'buttons') {
            return buttonSounds[0].id
        }
        if (type === 'touch') {
            return touchSounds[0].id
        }
        if (type === 'hybrid') {
            return hybridSounds[0].id
        }
    }

    const songList = currentUser?.songList

    const changeCurrentSong = ({ songId, songType, instrumentSound, deviceId, isChordProgression }: { songId?: string; songType?: SONG_TYPES; instrumentSound?: string; deviceId?: string; isChordProgression?: boolean }) => {
        const songObject = songList?.find((songObj) => songObj.id === songId)

        if (!deviceId) {
            connectedInstruments.forEach((instrument: Instrument) => {
                updateConnectedInstruments({
                    deviceId: instrument.deviceId,
                    serialNr: instrument.serialNr,
                    type: instrument.type,
                    batteryLevel: instrument.batteryLevel,
                    arrangement: instrument.arrangement,
                    volume: instrument.volume,
                    touchMode: instrument.type === 'touch' ? songObject?.touchMode : instrument.type === 'hybrid' ? songObject?.hybridMode : undefined,
                    currentSong: {
                        ...instrument.currentSong,
                        category: songObject?.category.toLowerCase(),
                        key: songObject?.keytone,
                        songType: songObject?.type,
                        id: songObject?.id,
                        sound: songType === SONG_TYPES.KEY ? (isChordProgression ? undefined : getDefaultSound(instrument.type)) : undefined,
                        touchMode: songObject?.touchMode,
                        hybridMode: songObject?.hybridMode
                    }
                })
            })
            setCurrentSong(songObject)

            if (songType === SONG_TYPES.KEY) {
                connectedInstruments.forEach((instrument: Instrument) => {
                    if (instrument.type === 'touch' || instrument.type === 'hybrid') {
                        changeInstrumentMode({
                            deviceId: instrument.deviceId,
                            touchMode: 'HARD'
                        })
                    }
                    if (isChordProgression && songObject) {
                        changeArrangement({
                            arrangement: songObject?.id,
                            deviceId: instrument.deviceId
                        })
                    } else {
                        changeArrangement({
                            arrangement: songObject?.keytone + '-' + songObject?.category + '-' + getDefaultSound(instrument.type),
                            deviceId: instrument.deviceId
                        })
                    }
                })
            } else if (songObject) {
                connectedInstruments.forEach((instrument: Instrument) => {
                    changeArrangement({
                        arrangement: songObject?.id,
                        deviceId: instrument.deviceId
                    })
                })
            }

            if (currentUser?.customer?.version === 1.1) {
                connectedInstruments.forEach((instrument: Instrument) => {
                    if (instrument.type === 'touch') {
                        if (songObject?.touchMode === 'EASY') {
                            changeInstrumentMode({
                                deviceId: instrument.deviceId,
                                touchMode: 'EASY'
                            })
                        }
                        if (songObject?.touchMode === 'BOTH' || songObject?.touchMode === 'HARD') {
                            changeInstrumentMode({
                                deviceId: instrument.deviceId,
                                touchMode: 'HARD'
                            })
                        }
                    }
                    if (instrument.type === 'hybrid') {
                        if (songObject?.hybridMode === 'EASY') {
                            changeInstrumentMode({
                                deviceId: instrument.deviceId,
                                touchMode: 'EASY'
                            })
                        }
                        if (songObject?.hybridMode === 'BOTH' || songObject?.hybridMode === 'HARD') {
                            changeInstrumentMode({
                                deviceId: instrument.deviceId,
                                touchMode: 'HARD'
                            })
                        }
                    }
                })
            } else {
                if (songObject?.touchMode === 'EASY') {
                    changeInstrumentMode({ touchMode: 'EASY' })
                }
                if (songObject?.touchMode === 'BOTH' || songObject?.touchMode === 'HARD') {
                    changeInstrumentMode({ touchMode: 'HARD' })
                }
            }
        }

        if (songType === SONG_TYPES.KEY && instrumentSound && deviceId) {
            const instrumentToUpdate = connectedInstruments.find((instrument: Instrument) => instrument.deviceId === deviceId)

            if (instrumentToUpdate) {
                updateConnectedInstruments({
                    ...instrumentToUpdate,
                    currentSong: {
                        ...instrumentToUpdate.currentSong,
                        sound: instrumentSound
                    }
                })
                changeArrangement({
                    arrangement: instrumentToUpdate?.currentSong?.key + '-' + instrumentToUpdate?.currentSong?.category + '-' + instrumentSound,
                    deviceId: deviceId
                })
                if (instrumentToUpdate.type === 'touch' || instrumentToUpdate.type === 'hybrid') {
                    changeInstrumentMode({
                        deviceId: instrumentToUpdate.deviceId,
                        touchMode: 'HARD'
                    })
                }
            }
        } else if (deviceId && songId) {
            const instrumentToUpdate = connectedInstruments.find((instrument: Instrument) => instrument.deviceId === deviceId)

            if (instrumentToUpdate) {
                updateConnectedInstruments({
                    ...instrumentToUpdate,
                    currentSong: {
                        ...instrumentToUpdate.currentSong,
                        category: songObject?.category.toLowerCase(),
                        key: songObject?.keytone,
                        songType: songObject?.type,
                        id: songObject?.id,
                        sound: songObject?.type === SONG_TYPES.KEY ? getDefaultSound(instrumentToUpdate.type) : undefined
                    }
                })
                if (songObject?.type === SONG_TYPES.KEY) {
                    if (instrumentToUpdate.type === 'touch' || instrumentToUpdate.type === 'hybrid') {
                        changeInstrumentMode({
                            deviceId: instrumentToUpdate.deviceId,
                            touchMode: 'HARD'
                        })
                    }
                    changeArrangement({
                        arrangement: songObject?.keytone + '-' + songObject?.category + '-' + getDefaultSound(instrumentToUpdate.type),
                        deviceId: instrumentToUpdate.deviceId
                    })
                } else {
                    //Change song for the specific instrument
                    changeArrangement({
                        arrangement: songId,
                        deviceId: deviceId
                    })

                    //Set correct touch mode for touch or hybrid instruments
                    if (currentUser?.customer?.version === 1.1) {
                        if (instrumentToUpdate.type === 'touch' || instrumentToUpdate.type === 'hybrid') {
                            if (songObject?.touchMode === 'EASY') {
                                changeInstrumentMode({
                                    deviceId: instrumentToUpdate.deviceId,
                                    touchMode: 'EASY'
                                })
                            }
                            if (songObject?.touchMode === 'BOTH' || songObject?.touchMode === 'HARD') {
                                changeInstrumentMode({
                                    deviceId: instrumentToUpdate.deviceId,
                                    touchMode: 'HARD'
                                })
                            }
                        }
                    } else {
                        if (songObject?.touchMode === 'EASY') {
                            changeInstrumentMode({ touchMode: 'EASY' })
                        }
                        if (songObject?.touchMode === 'BOTH' || songObject?.touchMode === 'HARD') {
                            changeInstrumentMode({ touchMode: 'HARD' })
                        }
                    }
                }
            }
        }
    }

    return (
        <CurrentSongContext.Provider
            value={{
                currentSong,
                changeCurrentSong
            }}
        >
            {children}
        </CurrentSongContext.Provider>
    )
}

export const useCurrentSong = () => useContext(CurrentSongContext)?.currentSong
export const useChangeCurrentSong = () => useContext(CurrentSongContext)?.changeCurrentSong
